import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, Calendar, DollarSign, Shield, Clock, CheckCircle } from 'lucide-react';

const MonthlyCabService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    { icon: DollarSign, title: 'Cost Effective', description: 'Save up to 40% compared to daily bookings' },
    { icon: Calendar, title: 'Fixed Schedule', description: 'Dedicated cab at your preferred timings' },
    { icon: Shield, title: 'Verified Drivers', description: 'Background checked professional drivers' },
    { icon: Clock, title: 'No Waiting Time', description: 'Your cab arrives on time, every time' },
  ];

  const packages = [
    {
      name: 'Basic Plan',
      duration: '8 hours/day',
      distance: '80 km/day',
      features: ['Monday to Friday', 'Single shift', 'AC sedan', 'Professional driver'],
      popular: false
    },
    {
      name: 'Premium Plan',
      duration: '10 hours/day',
      distance: '100 km/day',
      features: ['All 7 days', 'Flexible timings', 'SUV/Sedan choice', 'Priority support'],
      popular: true
    },
    {
      name: 'Executive Plan',
      duration: '12 hours/day',
      distance: '120 km/day',
      features: ['24/7 availability', 'Multiple trips', 'Luxury vehicles', 'Dedicated manager'],
      popular: false
    },
  ];

  const handleBookNow = () => {
    navigate('/booking-confirmation', {
      state: { serviceType: 'monthly' }
    });
  };

  return (
    <>
      <SEO
        title="Monthly Cab Service - Affordable Monthly Taxi Packages | Advait Tour and Travels"
        description="Book monthly cab service with fixed schedule and dedicated drivers. Cost-effective packages for office commute, daily travel in Chandigarh, Mohali, Panchkula. Save up to 40%."
        keywords="monthly cab service, monthly taxi package, office cab monthly, dedicated cab service, monthly car rental chandigarh"
        canonicalUrl={`${window.location?.origin}/monthly-cab-service`}
        ogTitle="Monthly Cab Service - Affordable Monthly Taxi Packages | Advait Tour and Travels"
        ogDescription="Book monthly cab service with fixed schedule and dedicated drivers. Cost-effective packages for office commute and daily travel."
        ogUrl={`${window.location?.origin}/monthly-cab-service`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Monthly Cab Service",
          "description": "Monthly taxi packages with dedicated drivers",
          "provider": {
            "@type": "Organization",
            "name": "Advait Tour and Travels"
          }
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Monthly Cab Service
              </h1>
              <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
                Affordable Monthly Packages for Your Daily Travel Needs
              </p>
              <button
                onClick={handleBookNow}
                className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold text-lg rounded-lg transition-all duration-200"
              >
                Book Monthly Package
              </button>
            </div>
          </div>

          {/* Features Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features?.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-blue-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature?.title}</h3>
                  <p className="text-gray-600">{feature?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Packages Section */}
          <div className="container mx-auto px-4 mb-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Choose Your Monthly Package</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {packages?.map((pkg, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden ${
                    pkg?.popular ? 'ring-4 ring-orange-400 transform scale-105' : ''
                  }`}
                >
                  {pkg?.popular && (
                    <div className="bg-orange-500 text-white text-center py-2 font-bold">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg?.name}</h3>
                    <div className="mb-6">
                      <p className="text-gray-600 mb-2">{pkg?.duration}</p>
                      <p className="text-gray-600">{pkg?.distance}</p>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {pkg?.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={handleBookNow}
                      className={`w-full py-3 rounded-lg font-bold transition-all duration-200 ${
                        pkg?.popular
                          ? 'bg-orange-500 hover:bg-orange-600 text-white' :'bg-blue-500 hover:bg-blue-600 text-white'
                      }`}
                    >
                      Select Plan
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Monthly Cab Service?</h2>
              
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  Looking for a hassle-free and economical solution for your daily commute? <strong>Advait Tour and Travels</strong> offers Monthly Cab Service designed to make your regular travel convenient and affordable. Whether you need transportation for office, business meetings, or daily errands, our monthly packages provide the perfect solution.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Benefits of Monthly Cab Service</h3>
                
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Significant Cost Savings:</strong> Save up to 40% compared to daily cab bookings. Fixed monthly rates with no surge pricing.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Dedicated Driver:</strong> Get a dedicated, professional driver who knows your route and preferences.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Fixed Schedule:</strong> Your cab arrives at your doorstep at the same time every day - no waiting, no delays.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Flexible Packages:</strong> Choose from various packages based on your daily travel distance and time requirements.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Well-Maintained Vehicles:</strong> Travel in clean, comfortable, and regularly serviced vehicles.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span><strong>Priority Support:</strong> Dedicated customer support for monthly subscribers with quick issue resolution.</span>
                  </li>
                </ul>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Who Should Choose Monthly Cab Service?</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="text-xl font-bold text-blue-900 mb-3">Perfect For</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Working professionals with regular office commute</li>
                      <li>• Business executives with frequent meetings</li>
                      <li>• Students attending college or coaching</li>
                      <li>• Senior citizens needing regular transportation</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-6">
                    <h4 className="text-xl font-bold text-orange-900 mb-3">Ideal Scenarios</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Daily office pickup and drop</li>
                      <li>• Regular hospital or clinic visits</li>
                      <li>• School or college transportation</li>
                      <li>• Multiple daily errands and appointments</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How to Book</h3>
                <ol className="space-y-3 ml-6 list-decimal">
                  <li><strong>Choose Your Package:</strong> Select the plan that best fits your daily travel needs.</li>
                  <li><strong>Provide Details:</strong> Share your pickup location, drop location, and preferred timings.</li>
                  <li><strong>Get Confirmation:</strong> Receive confirmation with driver details and vehicle information.</li>
                  <li><strong>Start Your Journey:</strong> Your dedicated cab will be ready from day one of your subscription.</li>
                </ol>

                <div className="bg-green-50 rounded-lg p-6 mt-8">
                  <h3 className="text-xl font-bold text-green-900 mb-3">Special Offer</h3>
                  <p className="text-gray-700">
                    Book a 3-month package and get 10% discount! Book a 6-month package and get 15% discount! Limited time offer - contact us today to avail this special pricing.
                  </p>
                </div>

                <p className="mt-8">
                  Make your daily commute stress-free and economical with <strong>Advait Tour and Travels</strong> Monthly Cab Service. Contact us today to discuss your requirements and get a customized quote.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default MonthlyCabService;